/**
 * TODO isMobile: true mode
 * TODO locale
 */
import "air-datepicker/air-datepicker.css";
import { useLocation } from "react-router";
import AirDatepicker from "air-datepicker";
import localeDe from "air-datepicker/locale/de";
import localeEn from "air-datepicker/locale/en";
import localeEs from "air-datepicker/locale/es";
import localePt from "air-datepicker/locale/pt";
import localeRu from "air-datepicker/locale/ru";
import clsx from "clsx";
import React, { useEffect, useRef } from "react";
import invariant from "tiny-invariant";

import Icon from "~/components/Icon";
import { isMobileDeviceClient } from "~/helpers/isMobileDevice.client";
import { getLocale } from "~/i18n";

interface Props {
  name: string;
  className?: string;
  placeholder?: string;
  value?: Date | null;
  ariaLabel?: string;
  onChange?: (date: Date) => void;
  options?: object | null;
  timepicker?: boolean;
  maxDate?: Date,
}


const DatePicker: React.FC<Props> = (
  {
    name,
    className = null,
    placeholder = "",
    value = null,
    ariaLabel = null,
    onChange = () => {},
    options,
    timepicker = false,
    maxDate = null,
  }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const altInputRef = useRef<HTMLInputElement>(null);
  const datePicker = useRef<AirDatepicker<HTMLInputElement>>();
  const locale = getLocale(useLocation().pathname);
  invariant(locale, "Locale is not defined");

  const timepickerRef = useRef<boolean>(timepicker);

  function pickLocale(locale: string) {
    switch (locale) {
      case "ru":
        return localeRu;
      case "es":
        return localeEs;
      case "de":
        return localeDe;
      case "pt":
        return localePt;
      default:
        return localeEn;
    }
  }
  // Start init
  useEffect(() => {
    if (inputRef.current === null) {
      console.log("Null ref");
    } else {
      datePicker.current = new AirDatepicker<HTMLInputElement>(inputRef.current, {
        ...options,
        altField: altInputRef.current ?? "",
        altFieldDateFormat: "yyyy-MM-dd",
        dateFormat(date) {
          const formattedDate = date.toLocaleDateString(locale, {
            year: 'numeric',
            day: '2-digit',
            month: 'long',
          }).replace(' г.', '');

          // Need to use ref here, or react will pass frozen snapshot of timepicker prop into callback
          if (!timepickerRef.current) {
            return formattedDate
          }

          const formattedTime = date.toLocaleTimeString(locale, {
            hour: '2-digit',
            minute: '2-digit',
          });

          return `${formattedDate} ${formattedTime}`;
        },
        locale: pickLocale(locale),
        isMobile: isMobileDeviceClient(),
        timepicker,
        maxDate: maxDate ?? "",
        onSelect: (e) => onChange(e.date as Date),
      });

      if (value !== null) {
        datePicker.current.selectDate(value);
      }
    }

    return () => {
      datePicker.current?.destroy();
    }
  }, []);

  useEffect(() => {
    datePicker.current?.update({
      locale: pickLocale(locale)
    });
  }, [locale]);

  useEffect(() => {
    timepickerRef.current = timepicker;
    if (datePicker.current) {
      datePicker.current?.update({ timepicker });
    }

    if (!timepicker && value !== null && datePicker.current) {
      const dateWithoutTime = new Date(value.getFullYear(), value.getMonth(), value.getDate());
      datePicker.current.selectDate(dateWithoutTime);
    }
  }, [timepicker]);

  useEffect(() => {
    // Update if props are changed
    datePicker.current?.update({ ...options });
  }, [options]);

  return (
    <div className={clsx(
      "relative dark:text-primary-300",
      className
    )}>
      <span className="absolute h-full p-4">
          <Icon v="calendar" />
      </span>
      <input
        ref={inputRef}
        type="text"
        id={name}
        // name={name}
        placeholder={placeholder}
        className={clsx(
          "px-11",
          {
            // "border-red-500 dark:border-red-500": isInvalid, // TODO Implement
            "invalid": false
          },
          className
        )}
        {...(ariaLabel ? { "aria-label": ariaLabel } : {})}
      />
      <input
        ref={altInputRef}
        type="hidden"
        name={name}
      />
    </div>
  );
};

export default DatePicker;
